import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Settings, Save, DollarSign, User, Lock, Eye, EyeOff, Trash2, AlertTriangle, Users, CreditCard, Download, Upload, Database, Zap, Phone, Droplets, FileText, Image, X, MessageCircle, Plus, Edit, Copy, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";
import { saveAs } from 'file-saver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  // Fee states for all products
  const [fees, setFees] = useState({
    pdam: "1500",
    pdam_digiflazz: "1500",
    token_listrik: "1500",
    tagihan_listrik: "1500",
    pulsa: "1500"
  });
  
  // Service status states (ON/OFF)
  const [serviceStatus, setServiceStatus] = useState({
    pdam: true,
    pdam_tebo: true,
    token_listrik: true,
    pulsa: true
  });
  const [isSavingServiceStatus, setIsSavingServiceStatus] = useState(false);
  // Peta Global edit toggle (ON/OFF)
  const [petaEditEnabled, setPetaEditEnabled] = useState(false);
  const [isSavingPetaEdit, setIsSavingPetaEdit] = useState(false);
  const [minTopup, setMinTopup] = useState("10000");
  const [isSavingMinTopup, setIsSavingMinTopup] = useState(false);
  
  const [currentUsername, setCurrentUsername] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Change username state
  const [newUsername, setNewUsername] = useState("");
  const [usernamePassword, setUsernamePassword] = useState("");
  const [isChangingUsername, setIsChangingUsername] = useState(false);
  
  // Change password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Reset data states
  const [isResettingCustomers, setIsResettingCustomers] = useState(false);
  const [isResettingTransactions, setIsResettingTransactions] = useState(false);

  // Export/Import states
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const importFileRef = useRef(null);

  // Receipt settings state
  const [receiptSettings, setReceiptSettings] = useState({
    show_customer_address: true,
    show_fee_loket: true,
    show_denda: true,
    show_kasir: true,
    show_total_tagihan: true,
    header_text: "PRODUK DIGITAL",
    footer_text: "Terima kasih atas pembayaran Anda",
    note_text: "Struk ini adalah bukti pembayaran yang sah",
    logo_url: null
  });
  const [isSavingReceipt, setIsSavingReceipt] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // WhatsApp Template states
  const [waTemplates, setWaTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: "", content: "", is_active: true });
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  // Auth Settings states
  const [authSettings, setAuthSettings] = useState({
    google_login_enabled: true,
    require_login_for_bills: true
  });
  const [isSavingAuthSettings, setIsSavingAuthSettings] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchWaTemplates();
  }, []);

  const fetchWaTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/wa-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaTemplates(response.data.templates || []);
    } catch (err) {
      console.error("Failed to fetch WA templates:", err);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      toast.error("Nama dan isi template harus diisi");
      return;
    }

    try {
      setIsSavingTemplate(true);
      const token = localStorage.getItem("admin_token");
      
      if (editingTemplate) {
        // Update existing template
        await axios.put(`${API}/admin/wa-templates/${editingTemplate.template_id}`, templateForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template berhasil diupdate");
      } else {
        // Create new template
        await axios.post(`${API}/admin/wa-templates`, templateForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template berhasil dibuat");
      }
      
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({ name: "", content: "", is_active: true });
      fetchWaTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan template");
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/wa-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template berhasil dihapus");
      fetchWaTemplates();
    } catch (err) {
      toast.error("Gagal menghapus template");
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      content: template.content,
      is_active: template.is_active
    });
    setShowTemplateModal(true);
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: "", content: "", is_active: true });
    setShowTemplateModal(true);
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Set fees from response
      setFees({
        pdam: response.data.fees?.pdam?.toString() || response.data.admin_fee?.toString() || "1500",
        pdam_digiflazz: response.data.fees?.pdam_digiflazz?.toString() || "1500",
        token_listrik: response.data.fees?.token_listrik?.toString() || "1500",
        tagihan_listrik: response.data.fees?.tagihan_listrik?.toString() || "1500",
        pulsa: response.data.fees?.pulsa?.toString() || "1500"
      });
      
      // Set service status from response
      if (response.data.service_status) {
        setServiceStatus({
          pdam: response.data.service_status.pdam ?? true,
          pdam_tebo: response.data.service_status.pdam_tebo ?? true,
          token_listrik: response.data.service_status.token_listrik ?? true,
          pulsa: response.data.service_status.pulsa ?? true
        });
      }

      // Set Peta Global edit toggle
      setPetaEditEnabled(response.data.peta_edit_enabled ?? false);
      setMinTopup((response.data.customer_min_topup ?? 10000).toString());
      
      // Set receipt settings from response
      if (response.data.receipt_settings) {
        setReceiptSettings(prev => ({ ...prev, ...response.data.receipt_settings }));
      }
      
      // Fetch auth settings separately
      try {
        const authRes = await axios.get(`${API}/auth-settings`);
        setAuthSettings({
          google_login_enabled: authRes.data.google_login_enabled ?? true,
          require_login_for_bills: authRes.data.require_login_for_bills ?? true
        });
      } catch (authErr) {
        console.error("Failed to fetch auth settings:", authErr);
      }
      
      setCurrentUsername(response.data.username || "admin");
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat pengaturan (unauthorized).");
      } else {
        toast.error("Gagal memuat pengaturan");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFees = async () => {
    // Validate all fees
    for (const [key, value] of Object.entries(fees)) {
      const fee = parseFloat(value);
      if (isNaN(fee) || fee < 0) {
        toast.error(`Masukkan biaya admin yang valid untuk ${key}`);
        return;
      }
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/settings`, {
        fees: {
          pdam: parseFloat(fees.pdam),
          pdam_digiflazz: parseFloat(fees.pdam_digiflazz),
          token_listrik: parseFloat(fees.token_listrik),
          tagihan_listrik: parseFloat(fees.tagihan_listrik),
          pulsa: parseFloat(fees.pulsa)
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Biaya admin berhasil disimpan");
    } catch (err) {
      toast.error("Gagal menyimpan pengaturan");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFeeChange = (type, value) => {
    setFees(prev => ({ ...prev, [type]: value }));
  };

  const handleSaveMinTopup = async () => {
    const v = parseFloat(minTopup);
    if (isNaN(v) || v < 0) { toast.error("Masukkan nominal yang valid"); return; }
    try {
      setIsSavingMinTopup(true);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/settings`, { customer_min_topup: v }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Minimal isi saldo berhasil disimpan");
    } catch (err) {
      toast.error("Gagal menyimpan minimal isi saldo");
    } finally {
      setIsSavingMinTopup(false);
    }
  };

  const handleServiceStatusChange = async (serviceKey) => {
    const newStatus = !serviceStatus[serviceKey];
    setServiceStatus(prev => ({ ...prev, [serviceKey]: newStatus }));
    
    try {
      setIsSavingServiceStatus(true);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/settings`, {
        service_status: {
          ...serviceStatus,
          [serviceKey]: newStatus
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Layanan ${newStatus ? 'diaktifkan' : 'dinonaktifkan'}`);
    } catch (err) {
      // Revert on error
      setServiceStatus(prev => ({ ...prev, [serviceKey]: !newStatus }));
      toast.error("Gagal mengubah status layanan");
    } finally {
      setIsSavingServiceStatus(false);
    }
  };

  // Handle Peta Global edit toggle (ON = semua role bisa edit/pindah/hapus titik)
  const handlePetaEditToggle = async () => {
    const newValue = !petaEditEnabled;
    setPetaEditEnabled(newValue);
    try {
      setIsSavingPetaEdit(true);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/settings`, { peta_edit_enabled: newValue }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newValue ? "Edit Peta Global diaktifkan — semua petugas bisa edit/hapus titik." : "Edit Peta Global dikunci — tidak ada yang bisa edit/hapus titik.");
    } catch (err) {
      setPetaEditEnabled(!newValue);
      toast.error("Gagal mengubah pengaturan edit peta");
    } finally {
      setIsSavingPetaEdit(false);
    }
  };

  // Handle auth settings toggle
  const handleAuthSettingChange = async (settingKey) => {    const newValue = !authSettings[settingKey];
    setAuthSettings(prev => ({ ...prev, [settingKey]: newValue }));
    
    try {
      setIsSavingAuthSettings(true);
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/auth-settings`, {
        ...authSettings,
        [settingKey]: newValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settingLabels = {
        google_login_enabled: newValue ? "Login Google diaktifkan" : "Login Google dinonaktifkan",
        require_login_for_bills: newValue ? "Wajib login untuk cek tagihan" : "Tagihan bisa dicek tanpa login"
      };
      toast.success(settingLabels[settingKey]);
    } catch (err) {
      // Revert on error
      setAuthSettings(prev => ({ ...prev, [settingKey]: !newValue }));
      toast.error("Gagal mengubah pengaturan login");
    } finally {
      setIsSavingAuthSettings(false);
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    
    if (!newUsername.trim()) {
      toast.error("Masukkan username baru");
      return;
    }
    if (!usernamePassword) {
      toast.error("Masukkan password untuk konfirmasi");
      return;
    }

    try {
      setIsChangingUsername(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/change-username`, {
        new_username: newUsername.trim(),
        password: usernamePassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update token and username
      localStorage.setItem("admin_token", response.data.new_token);
      setCurrentUsername(response.data.new_username);
      setNewUsername("");
      setUsernamePassword("");
      
      toast.success("Username berhasil diubah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah username");
    } finally {
      setIsChangingUsername(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error("Masukkan password saat ini");
      return;
    }
    if (!newPassword) {
      toast.error("Masukkan password baru");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password baru minimal 6 karakter");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }

    try {
      setIsChangingPassword(true);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      toast.success("Password berhasil diubah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleResetCustomers = async () => {
    try {
      setIsResettingCustomers(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/reset-data/customers`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Data pelanggan berhasil dihapus (${response.data.deleted.bills} tagihan)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus data pelanggan");
    } finally {
      setIsResettingCustomers(false);
    }
  };

  const handleResetTransactions = async () => {
    try {
      setIsResettingTransactions(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/reset-data/transactions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Data transaksi berhasil dihapus (${response.data.deleted.payments} pembayaran)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus data transaksi");
    } finally {
      setIsResettingTransactions(false);
    }
  };

  // Export data to JSON file
  const handleExportData = async () => {
    try {
      setIsExporting(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/export-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download JSON file using file-saver
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json;charset=utf-8' });
      
      // Generate filename with date
      const date = new Date().toISOString().slice(0, 10);
      const filename = `backup_data_${date}.json`;
      
      // Use file-saver library for reliable download
      saveAs(blob, filename);
      
      const { bills_count, payments_count } = response.data.data;
      toast.success(`File "${filename}" sedang didownload... (${bills_count} tagihan, ${payments_count} pembayaran)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal export data");
    } finally {
      setIsExporting(false);
    }
  };

  // Import data from JSON file
  const handleImportData = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error("File harus berformat JSON");
      return;
    }
    
    try {
      setIsImporting(true);
      const token = localStorage.getItem("admin_token");
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/admin/import-data`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const { bills, payments } = response.data;
      toast.success(`Import berhasil! ${bills?.inserted || 0} tagihan baru, ${bills?.updated || 0} diupdate, ${payments?.inserted || 0} pembayaran`);
      
      // Reset file input
      if (importFileRef.current) {
        importFileRef.current.value = '';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal import data");
    } finally {
      setIsImporting(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(num);
  };

  const handleSaveReceiptSettings = async () => {
    try {
      setIsSavingReceipt(true);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/settings`, {
        receipt_settings: receiptSettings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pengaturan struk berhasil disimpan");
    } catch (err) {
      toast.error("Gagal menyimpan pengaturan struk");
    } finally {
      setIsSavingReceipt(false);
    }
  };

  const handleReceiptToggle = (key) => {
    setReceiptSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleReceiptTextChange = (key, value) => {
    setReceiptSettings(prev => ({ ...prev, [key]: value }));
  };

  // Upload logo handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("File harus berformat PNG, JPG, atau WEBP");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 2MB");
      return;
    }

    try {
      setIsUploadingLogo(true);
      const token = localStorage.getItem("admin_token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/admin/upload-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setReceiptSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success("Logo berhasil diupload!");

      // Reset file input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Delete logo handler
  const handleDeleteLogo = async () => {
    try {
      setIsUploadingLogo(true);
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/delete-logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReceiptSettings(prev => ({ ...prev, logo_url: null }));
      toast.success("Logo berhasil dihapus");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  return (
    <AdminLayout title="Pengaturan">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-2xl mx-auto space-y-6"
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Auth Settings - Login Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Pengaturan Login
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500 -mt-2">
                  Atur metode login dan akses untuk pengguna
                </p>
                
                {/* Google Login Toggle */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Login dengan Google</h4>
                      <p className="text-xs text-slate-500">
                        {authSettings.google_login_enabled 
                          ? "Pengguna bisa login menggunakan akun Google" 
                          : "Login Google dinonaktifkan, harus login dengan HP & password"
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAuthSettingChange('google_login_enabled')}
                    disabled={isSavingAuthSettings}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      authSettings.google_login_enabled ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-google-login"
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      authSettings.google_login_enabled ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Require Login for Bills Toggle */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Wajib Login untuk Cek Tagihan</h4>
                      <p className="text-xs text-slate-500">
                        {authSettings.require_login_for_bills 
                          ? "Pengguna harus login sebelum bisa cek tagihan" 
                          : "Siapapun bisa cek tagihan tanpa login"
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAuthSettingChange('require_login_for_bills')}
                    disabled={isSavingAuthSettings}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      authSettings.require_login_for_bills ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-require-login"
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      authSettings.require_login_for_bills ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {!authSettings.google_login_enabled && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Perhatian:</strong> Login Google dinonaktifkan. Semua pengguna harus login menggunakan nomor HP dan password.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Service Status ON/OFF */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Status Layanan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500 -mt-2">
                  Aktifkan atau nonaktifkan layanan. Jika OFF, pengguna akan melihat pesan "Sedang perbaikan server"
                </p>
                
                {/* PDAM Toggle */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                      <Droplets className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Tagihan PDAM</h4>
                      <p className="text-xs text-slate-500">Pembayaran tagihan air lokal</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleServiceStatusChange('pdam')}
                    disabled={isSavingServiceStatus}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      serviceStatus.pdam ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-pdam"
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      serviceStatus.pdam ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* PDAM Tebo Toggle */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <Droplets className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">PDAM Tebo Online</h4>
                      <p className="text-xs text-slate-500">Cek & bayar tagihan PDAM Tebo</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleServiceStatusChange('pdam_tebo')}
                    disabled={isSavingServiceStatus}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      serviceStatus.pdam_tebo ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-pdam-tebo"
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      serviceStatus.pdam_tebo ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Token Listrik Toggle */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Token Listrik</h4>
                      <p className="text-xs text-slate-500">Pembelian token PLN prabayar</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleServiceStatusChange('token_listrik')}
                    disabled={isSavingServiceStatus}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      serviceStatus.token_listrik ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-token-listrik"
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      serviceStatus.token_listrik ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Pulsa Toggle */}
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Pulsa & Data</h4>
                      <p className="text-xs text-slate-500">Isi ulang pulsa dan paket data</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleServiceStatusChange('pulsa')}
                    disabled={isSavingServiceStatus}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      serviceStatus.pulsa ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-pulsa"
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      serviceStatus.pulsa ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Peta Global — Izin Edit/Hapus Titik */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Izin Edit Peta Global
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500 -mt-2">
                  Saklar tunggal untuk mengunci/membuka edit titik di Peta Global. Tidak perlu kode lagi.
                </p>
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${petaEditEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <MapPin className={`w-5 h-5 ${petaEditEnabled ? 'text-green-600' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Izinkan Edit / Pindah / Hapus Titik</h4>
                      <p className="text-xs text-slate-500">
                        {petaEditEnabled
                          ? 'ON: semua role bisa edit, pindah & hapus titik tanpa kode'
                          : 'OFF: terkunci — tidak ada yang bisa edit atau hapus titik'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handlePetaEditToggle}
                    disabled={isSavingPetaEdit}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      petaEditEnabled ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-peta-edit"
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      petaEditEnabled ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Biaya Admin */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Biaya Admin
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <p className="text-sm text-slate-500 -mt-2">
                  Atur biaya admin untuk setiap jenis layanan
                </p>
                
                {/* PDAM Fee */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                      <Droplets className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Tagihan PDAM</h4>
                      <p className="text-xs text-slate-500">Per rekening/tagihan</p>
                    </div>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      value={fees.pdam}
                      onChange={(e) => handleFeeChange('pdam', e.target.value)}
                      className="h-11 pl-10"
                      placeholder="2000"
                      data-testid="fee-pdam-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Preview: {formatCurrency(fees.pdam)}</p>
                </div>

                {/* Tagihan PDAM (DigiFlazz) Fee */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Droplets className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Tagihan PDAM (DigiFlazz)</h4>
                      <p className="text-xs text-slate-500">Biaya admin layanan PDAM via DigiFlazz</p>
                    </div>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      value={fees.pdam_digiflazz}
                      onChange={(e) => handleFeeChange('pdam_digiflazz', e.target.value)}
                      className="h-11 pl-10"
                      placeholder="1500"
                      data-testid="fee-pdam-digiflazz-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Preview: {formatCurrency(fees.pdam_digiflazz)}</p>
                </div>

                {/* Token Listrik Fee */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Token Listrik</h4>
                      <p className="text-xs text-slate-500">Per transaksi pembelian</p>
                    </div>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      value={fees.token_listrik}
                      onChange={(e) => handleFeeChange('token_listrik', e.target.value)}
                      className="h-11 pl-10"
                      placeholder="2500"
                      data-testid="fee-token-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Preview: {formatCurrency(fees.token_listrik)}</p>
                </div>

                {/* Tagihan Listrik Fee */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Tagihan Listrik</h4>
                      <p className="text-xs text-slate-500">Per tagihan/bulan</p>
                    </div>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      value={fees.tagihan_listrik}
                      onChange={(e) => handleFeeChange('tagihan_listrik', e.target.value)}
                      className="h-11 pl-10"
                      placeholder="2500"
                      data-testid="fee-tagihan-listrik-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Preview: {formatCurrency(fees.tagihan_listrik)}</p>
                </div>

                {/* Pulsa Fee */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Pulsa & Data</h4>
                      <p className="text-xs text-slate-500">Per transaksi isi pulsa</p>
                    </div>
                  </div>
                  <div className="relative">
                    <Input
                      type="number"
                      value={fees.pulsa}
                      onChange={(e) => handleFeeChange('pulsa', e.target.value)}
                      className="h-11 pl-10"
                      placeholder="1000"
                      data-testid="fee-pulsa-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Preview: {formatCurrency(fees.pulsa)}</p>
                </div>

                <Button
                  onClick={handleSaveFees}
                  disabled={isSaving}
                  className="w-full h-11 btn-flow gap-2"
                  data-testid="save-fees-btn"
                >
                  {isSaving ? <div className="spinner w-5 h-5" /> : <><Save className="w-4 h-4" /> Simpan Semua Fee</>}
                </Button>
              </CardContent>
            </Card>

            {/* Minimal Isi Saldo Pelanggan */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Minimal Isi Saldo Pelanggan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500 -mt-2">Nominal terkecil yang boleh diisi pelanggan saat top-up saldo.</p>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Minimal Top-Up</label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={minTopup}
                      onChange={(e) => setMinTopup(e.target.value)}
                      className="h-11 pl-10"
                      placeholder="10000"
                      data-testid="min-topup-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Preview: {formatCurrency(minTopup)}</p>
                </div>
                <Button
                  onClick={handleSaveMinTopup}
                  disabled={isSavingMinTopup}
                  className="w-full h-11 btn-flow gap-2"
                  data-testid="save-min-topup-btn"
                >
                  {isSavingMinTopup ? <div className="spinner w-5 h-5" /> : <><Save className="w-4 h-4" /> Simpan Minimal Saldo</>}
                </Button>
              </CardContent>
            </Card>

            {/* Ubah Username */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Pengaturan Struk Pembayaran
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <p className="text-sm text-slate-500 -mt-2">
                  Atur tampilan struk pembayaran yang dilihat oleh petugas/agen
                </p>

                {/* Toggle options */}
                <div className="space-y-3">
                  {[
                    { key: 'show_customer_address', label: 'Tampilkan Alamat Pelanggan', desc: 'Alamat di struk pembayaran' },
                    { key: 'show_fee_loket', label: 'Tampilkan Biaya Admin', desc: 'Detail biaya admin per periode' },
                    { key: 'show_kasir', label: 'Tampilkan Nama Kasir', desc: 'Nama petugas/agen di struk' },
                    { key: 'show_total_tagihan', label: 'Tampilkan Total Tagihan', desc: 'Total tagihan sebelum biaya admin (jika OFF hanya subtotal)' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                      <div>
                        <h4 className="font-medium text-slate-800 text-sm">{item.label}</h4>
                        <p className="text-xs text-slate-500">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => handleReceiptToggle(item.key)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          receiptSettings[item.key] ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                        data-testid={`toggle-receipt-${item.key}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          receiptSettings[item.key] ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Text fields */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Header Struk</label>
                    <Input
                      value={receiptSettings.header_text}
                      onChange={(e) => handleReceiptTextChange('header_text', e.target.value)}
                      className="h-10"
                      placeholder="PRODUK DIGITAL"
                      data-testid="receipt-header-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Footer Struk</label>
                    <Input
                      value={receiptSettings.footer_text}
                      onChange={(e) => handleReceiptTextChange('footer_text', e.target.value)}
                      className="h-10"
                      placeholder="Terima kasih atas pembayaran Anda"
                      data-testid="receipt-footer-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Catatan Struk</label>
                    <Input
                      value={receiptSettings.note_text}
                      onChange={(e) => handleReceiptTextChange('note_text', e.target.value)}
                      className="h-10"
                      placeholder="Struk ini adalah bukti pembayaran yang sah"
                      data-testid="receipt-note-input"
                    />
                  </div>
                </div>

                {/* Logo Upload Section */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Image className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Logo Struk</h4>
                      <p className="text-xs text-slate-500">Logo akan muncul di struk saat pembayaran lunas (share & print)</p>
                    </div>
                  </div>

                  {/* Logo Preview */}
                  {receiptSettings.logo_url ? (
                    <div className="mb-3">
                      <div className="relative inline-block">
                        <img 
                          src={receiptSettings.logo_url} 
                          alt="Logo Struk" 
                          className="w-24 h-24 object-contain border border-slate-200 rounded-lg bg-white p-2"
                        />
                        <button
                          onClick={handleDeleteLogo}
                          disabled={isUploadingLogo}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md"
                          data-testid="delete-logo-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-green-600 mt-2">Logo aktif - akan tampil di struk</p>
                    </div>
                  ) : (
                    <div className="mb-3 p-4 border-2 border-dashed border-slate-200 rounded-lg text-center">
                      <Image className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Belum ada logo</p>
                    </div>
                  )}

                  {/* Upload Button */}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    ref={logoInputRef}
                    data-testid="logo-upload-input"
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      variant="outline"
                      className="w-full border-purple-300 text-purple-700 hover:bg-purple-50 cursor-pointer"
                      disabled={isUploadingLogo}
                      asChild
                    >
                      <span>
                        {isUploadingLogo ? (
                          <div className="spinner w-4 h-4 mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {receiptSettings.logo_url ? 'Ganti Logo' : 'Upload Logo'}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-slate-400 mt-2 text-center">Format: PNG, JPG, WEBP. Maksimal 2MB</p>
                </div>

                <Button
                  onClick={handleSaveReceiptSettings}
                  disabled={isSavingReceipt}
                  className="w-full h-11 btn-flow gap-2"
                  data-testid="save-receipt-settings-btn"
                >
                  {isSavingReceipt ? <div className="spinner w-5 h-5" /> : <><Save className="w-4 h-4" /> Simpan Pengaturan Struk</>}
                </Button>
              </CardContent>
            </Card>

            {/* WhatsApp Template Settings */}
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-green-700">
                  <MessageCircle className="w-5 h-5" />
                  Template WhatsApp (PDAM Tebo)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-700">
                    Buat template pesan WhatsApp untuk share tagihan PDAM Tebo. Gunakan placeholder seperti:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['{nama_pelanggan}', '{id_pelanggan}', '{alamat}', '{jumlah_tagihan}', '{total_tagihan}', '{periode}'].map(placeholder => (
                      <button
                        key={placeholder}
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(placeholder);
                          toast.success(`"${placeholder}" disalin!`);
                        }}
                        className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-mono hover:bg-green-200 transition-colors flex items-center gap-1"
                      >
                        {placeholder}
                        <Copy className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Template List */}
                {isLoadingTemplates ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="spinner w-6 h-6" />
                  </div>
                ) : waTemplates.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                    <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Belum ada template</p>
                    <p className="text-slate-400 text-xs">Buat template pertama Anda</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {waTemplates.map((template) => (
                      <div 
                        key={template.template_id} 
                        className={`border rounded-lg p-4 ${template.is_active ? 'border-green-200 bg-green-50/50' : 'border-slate-200 bg-slate-50'}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-800">{template.name}</h4>
                            {template.is_active ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">Aktif</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-slate-200 text-slate-500 rounded-full text-xs">Nonaktif</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditTemplate(template)}
                              className="h-8 w-8 p-0"
                              data-testid={`edit-template-${template.template_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                  data-testid={`delete-template-${template.template_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Hapus Template?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Template "{template.name}" akan dihapus secara permanen. Tindakan ini tidak dapat dibatalkan.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Batal</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteTemplate(template.template_id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Ya, Hapus
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-3">{template.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  onClick={openNewTemplate}
                  className="w-full h-11 gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="add-template-btn"
                >
                  <Plus className="w-4 h-4" /> Tambah Template Baru
                </Button>
              </CardContent>
            </Card>

            {/* Template Modal */}
            {showTemplateModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4">
                    <h3 className="font-bold text-lg">
                      {editingTemplate ? 'Edit Template' : 'Buat Template Baru'}
                    </h3>
                    <p className="text-sm opacity-90">Template pesan untuk share tagihan ke WhatsApp</p>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Nama Template
                      </label>
                      <Input
                        value={templateForm.name}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Contoh: Pengingat Tagihan"
                        className="h-11"
                        data-testid="template-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Isi Template
                      </label>
                      <textarea
                        value={templateForm.content}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
                        placeholder={`Contoh:\n📋 *TAGIHAN PDAM TIRTA MUARO TEBO*\n\nYth. {nama_pelanggan}\nID: {id_pelanggan}\n\nTotal Tagihan: {total_tagihan}\n\n⚠️ Rekening ini berlaku tanggal 1 s/d 20.\nHubungi petugas untuk pembayaran.`}
                        className="w-full h-40 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                        data-testid="template-content-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Gunakan placeholder: {'{nama_pelanggan}'}, {'{id_pelanggan}'}, {'{alamat}'}, {'{jumlah_tagihan}'}, {'{total_tagihan}'}, {'{periode}'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-600 rounded-lg">
                      <div>
                        <h4 className="font-medium text-slate-800 dark:text-white text-sm">Status Aktif</h4>
                        <p className="text-xs text-slate-500">Template aktif akan tampil saat share tagihan</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTemplateForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          templateForm.is_active ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                        data-testid="template-active-toggle"
                      >
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          templateForm.is_active ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button
                        variant="outline"
                        className="flex-1 h-11"
                        onClick={() => {
                          setShowTemplateModal(false);
                          setEditingTemplate(null);
                          setTemplateForm({ name: "", content: "", is_active: true });
                        }}
                        disabled={isSavingTemplate}
                      >
                        Batal
                      </Button>
                      <Button
                        className="flex-1 h-11 bg-green-600 hover:bg-green-700"
                        onClick={handleSaveTemplate}
                        disabled={isSavingTemplate}
                        data-testid="save-template-btn"
                      >
                        {isSavingTemplate ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        {editingTemplate ? 'Update' : 'Simpan'}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Ubah Username */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Ubah Username
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangeUsername} className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-slate-600">
                      Username saat ini: <span className="font-semibold text-slate-800">{currentUsername}</span>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Username Baru
                    </label>
                    <div className="relative">
                      <Input
                        type="text"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        className="h-11 pl-11"
                        placeholder="Masukkan username baru"
                        data-testid="new-username-input"
                      />
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Password (untuk konfirmasi)
                    </label>
                    <div className="relative">
                      <Input
                        type="password"
                        value={usernamePassword}
                        onChange={(e) => setUsernamePassword(e.target.value)}
                        className="h-11 pl-11"
                        placeholder="Masukkan password Anda"
                        data-testid="username-password-input"
                      />
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isChangingUsername}
                    className="w-full h-11 gap-2"
                    variant="outline"
                    data-testid="change-username-btn"
                  >
                    {isChangingUsername ? <div className="spinner w-5 h-5" /> : "Ubah Username"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Ubah Password */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Ubah Password
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Password Saat Ini
                    </label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="h-11 pl-11 pr-11"
                        placeholder="Masukkan password saat ini"
                        data-testid="current-password-input"
                      />
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Password Baru
                    </label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="h-11 pl-11 pr-11"
                        placeholder="Minimal 6 karakter"
                        data-testid="new-password-input"
                      />
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Konfirmasi Password Baru
                    </label>
                    <div className="relative">
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="h-11 pl-11"
                        placeholder="Ulangi password baru"
                        data-testid="confirm-password-input"
                      />
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isChangingPassword}
                    className="w-full h-11 gap-2"
                    variant="outline"
                    data-testid="change-password-btn"
                  >
                    {isChangingPassword ? <div className="spinner w-5 h-5" /> : "Ubah Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Export/Import Data */}
            <Card className="border-sky-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-sky-700">
                  <Database className="w-5 h-5" />
                  Export / Import Data
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-sky-700">
                    Gunakan fitur ini untuk backup data atau memindahkan data dari Preview ke Production.
                  </p>
                </div>
                
                {/* Export Data */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Download className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Export Data</h4>
                      <p className="text-sm text-slate-500">Download semua data tagihan & pembayaran dalam format JSON.</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={handleExportData}
                    disabled={isExporting}
                    data-testid="export-data-btn"
                  >
                    {isExporting ? <div className="spinner w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                    Export Data ke JSON
                  </Button>
                </div>

                {/* Import Data */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Upload className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Import Data</h4>
                      <p className="text-sm text-slate-500">Upload file JSON hasil export untuk menambahkan data.</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportData}
                    className="hidden"
                    id="import-file"
                    ref={importFileRef}
                    data-testid="import-file-input"
                  />
                  <label htmlFor="import-file">
                    <Button
                      variant="outline"
                      className="w-full border-blue-300 text-blue-700 hover:bg-blue-50 cursor-pointer"
                      disabled={isImporting}
                      asChild
                    >
                      <span>
                        {isImporting ? <div className="spinner w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Import Data dari JSON
                      </span>
                    </Button>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Reset Data */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-red-700">
                  <Trash2 className="w-5 h-5" />
                  Reset Data Aplikasi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700">
                      <strong>Peringatan:</strong> Tindakan ini tidak dapat dibatalkan. Pastikan Anda sudah melakukan backup data jika diperlukan.
                    </p>
                  </div>
                </div>
                
                {/* Reset Customer Data */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Reset Data Pelanggan</h4>
                      <p className="text-sm text-slate-500">Hapus semua data tagihan dari PDF. Setelah dihapus, Anda bisa upload PDF tagihan terbaru.</p>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                        disabled={isResettingCustomers}
                        data-testid="reset-customers-btn"
                      >
                        {isResettingCustomers ? <div className="spinner w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Reset Data Pelanggan
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Data Pelanggan?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Semua data tagihan dari PDF akan dihapus secara permanen. Anda perlu upload ulang PDF tagihan setelah ini. Tindakan ini tidak dapat dibatalkan.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleResetCustomers}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          Ya, Hapus Data Pelanggan
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                {/* Reset Transaction Data */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Reset Data Transaksi</h4>
                      <p className="text-sm text-slate-500">Hapus semua riwayat pembayaran dan reset status tagihan. Berguna untuk membersihkan history lama.</p>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full border-red-300 text-red-700 hover:bg-red-50"
                        disabled={isResettingTransactions}
                        data-testid="reset-transactions-btn"
                      >
                        {isResettingTransactions ? <div className="spinner w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Reset Data Transaksi
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Data Transaksi?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Semua riwayat pembayaran akan dihapus dan status tagihan akan di-reset ke &quot;belum dibayar&quot;. Tindakan ini tidak dapat dibatalkan.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleResetTransactions}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Ya, Hapus Data Transaksi
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>

            {/* Info */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
              <h4 className="font-medium text-sky-800 mb-2">Informasi Keamanan</h4>
              <ul className="text-sm text-sky-700 space-y-1">
                <li>• Gunakan password yang kuat (minimal 6 karakter)</li>
                <li>• Jangan bagikan kredensial admin kepada orang lain</li>
                <li>• Ubah password secara berkala untuk keamanan</li>
              </ul>
            </div>
          </>
        )}
      </motion.div>
    </AdminLayout>
  );
}
